import { Button, Card, CardContent, Input, Label, Loader } from "@devhop/ui";
import { useEffect, useMemo, useState } from "react";
import type { PosSalesByCurrencyReportQueryDTO } from "@/modules/merchant/domain/contracts";
import { canUseMerchantPermission } from "@/modules/merchant/presentation/api/client";
import { useMerchantShopsStore } from "@/modules/merchant/presentation/store/merchant-shops-store";
import { useMerchantShops } from "@/modules/merchant/presentation/store/use-merchant-shops";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import { formatPrice, SHOP_CURRENCIES } from "@/shared/lib/currency";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import {
  usePosSalesByBranchReportQuery,
  useShopBranchesListQuery,
} from "../api/queries";

/** ສະຫຼຸບຍອດຂາຍແຍກສາຂາ — ຕ້ອງເປີດ multi_branch (API + route guard) */
export function PosSalesByBranchReportSection({
  shopId,
}: {
  shopId: string | undefined;
}) {
  const { currentShop } = useMerchantShops(shopId);
  const shops = useMerchantShopsStore((s) => s.shops);

  const membershipsForShop = useMemo(
    () => (shopId ? shops.filter((x) => x.shopId === shopId) : []),
    [shops, shopId],
  );

  const hasShopWideBranchAccess = useMemo(
    () => membershipsForShop.some((m) => m.branchId == null),
    [membershipsForShop],
  );

  const accessibleScopedBranches = useMemo(() => {
    const m = new Map<string, string>();
    for (const row of membershipsForShop) {
      if (row.branchId) {
        m.set(
          row.branchId,
          row.branchName?.trim() || `${row.branchId.slice(0, 8)}…`,
        );
      }
    }
    return [...m.entries()].map(([id, name]) => ({ id, name }));
  }, [membershipsForShop]);

  const canViewSalesReport =
    shopId != null &&
    canUseMerchantPermission(
      currentShop?.permissions ?? [],
      MerchantPermissions.pos.read,
      currentShop?.verifiedLevel ?? null,
    );

  const canListBranchesForReport =
    shopId != null &&
    canUseMerchantPermission(
      currentShop?.permissions ?? [],
      MerchantPermissions.shop_staff.read,
      currentShop?.verifiedLevel ?? null,
    );

  const [reportFrom, setReportFrom] = useState("");
  const [reportTo, setReportTo] = useState("");
  const [reportBranchId, setReportBranchId] = useState<string>("__all__");

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset ສາຂາເມື່ອປ່ຽນຮ້ານ
  useEffect(() => {
    setReportBranchId("__all__");
  }, [shopId]);

  const branchesListQuery = useShopBranchesListQuery(
    canViewSalesReport && hasShopWideBranchAccess && canListBranchesForReport
      ? shopId
      : undefined,
  );

  const reportQuery = useMemo((): Partial<PosSalesByCurrencyReportQueryDTO> => {
    const q: Partial<PosSalesByCurrencyReportQueryDTO> = {};
    if (reportFrom.trim()) q.createdFrom = reportFrom.trim();
    if (reportTo.trim()) q.createdTo = reportTo.trim();

    if (hasShopWideBranchAccess) {
      if (
        canListBranchesForReport &&
        reportBranchId !== "__all__" &&
        reportBranchId.trim() !== ""
      ) {
        q.branchId = reportBranchId.trim();
      }
    } else if (accessibleScopedBranches.length > 1) {
      if (reportBranchId !== "__all__" && reportBranchId.trim() !== "") {
        q.branchId = reportBranchId.trim();
      }
    }
    return q;
  }, [
    reportFrom,
    reportTo,
    reportBranchId,
    hasShopWideBranchAccess,
    canListBranchesForReport,
    accessibleScopedBranches.length,
  ]);

  const branchReportSelectOptions = useMemo(() => {
    if (hasShopWideBranchAccess && canListBranchesForReport) {
      const items = branchesListQuery.data ?? [];
      return [
        { value: "__all__", label: "ທຸກສາຂາ" },
        ...items.map((b) => ({
          value: b.id,
          label:
            (b.name.trim() || `${b.id.slice(0, 8)}…`) +
            (b.isActive ? "" : " · ປິດ"),
        })),
      ];
    }
    if (!hasShopWideBranchAccess && accessibleScopedBranches.length > 1) {
      return [
        { value: "__all__", label: "ທຸກສາຂາທີ່ມີສິດ" },
        ...accessibleScopedBranches.map((b) => ({
          value: b.id,
          label: b.name,
        })),
      ];
    }
    return [];
  }, [
    hasShopWideBranchAccess,
    canListBranchesForReport,
    branchesListQuery.data,
    accessibleScopedBranches,
  ]);

  useEffect(() => {
    if (reportBranchId === "__all__") return;
    const opts = branchReportSelectOptions;
    if (opts.length === 0) return;
    if (!opts.some((o) => o.value === reportBranchId)) {
      setReportBranchId("__all__");
    }
  }, [branchReportSelectOptions, reportBranchId]);

  const reportBranchScopeLabel = useMemo(() => {
    if (reportBranchId === "__all__") {
      if (hasShopWideBranchAccess) return "ທຸກສາຂາ";
      if (accessibleScopedBranches.length <= 1) {
        return accessibleScopedBranches[0]?.name ?? "ສາຂາ";
      }
      return `ສາຂາທີ່ມີສິດ (${accessibleScopedBranches.length})`;
    }
    if (hasShopWideBranchAccess) {
      return (
        branchesListQuery.data
          ?.find((b) => b.id === reportBranchId)
          ?.name?.trim() || "ສາຂາທີ່ເລືອກ"
      );
    }
    return (
      accessibleScopedBranches.find((b) => b.id === reportBranchId)?.name ??
      "ສາຂາທີ່ເລືອກ"
    );
  }, [
    reportBranchId,
    hasShopWideBranchAccess,
    accessibleScopedBranches,
    branchesListQuery.data,
  ]);

  const byBranch = usePosSalesByBranchReportQuery(
    shopId,
    reportQuery,
    canViewSalesReport,
  );

  if (!canViewSalesReport) {
    return (
      <Card>
        <CardContent className="py-6">
          <p className="text-muted-foreground text-sm">
            ບໍ່ມີສິດທີ່ຈຳເປັນ (pos.read) ເພື່ອເບິ່ງລາຍງານນີ້.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="space-y-4">
        <div>
          <h2 className="font-heading font-semibold text-foreground text-sm">
            ຍອດຂາຍແຍກສາຂາ
          </h2>
          <p className="mt-1 text-muted-foreground text-xs">
            ເປຣຢີບຽບຍອດຕາມສາຂາ (ສະກຸນຕາມບິນ) — ຕ້ອງເປີດຟີເຈີ multi_branch.
          </p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
          {hasShopWideBranchAccess && canListBranchesForReport ? (
            <div className="min-w-0 space-y-1.5 sm:min-w-56">
              <Label>ກອງສາຂາ</Label>
              <SimpleSelect
                value={reportBranchId}
                onValueChange={setReportBranchId}
                options={branchReportSelectOptions}
                placeholder="ເລືອກສາຂາ"
                disabled={branchesListQuery.isLoading}
                className="w-full font-medium"
              />
              {branchesListQuery.isError ? (
                <p className="text-[11px] text-destructive">
                  ໂຫຼດລາຍການສາຂາບໍ່ສຳເລັດ
                </p>
              ) : null}
            </div>
          ) : hasShopWideBranchAccess && !canListBranchesForReport ? (
            <p className="text-[11px] text-muted-foreground sm:max-w-xs sm:self-end">
              ບໍ່ມີສິດອ່ານລາຍການສາຂາ — ສະແດງທຸກສາຂາຂອງຮ້ານ.
            </p>
          ) : !hasShopWideBranchAccess && accessibleScopedBranches.length > 1 ? (
            <div className="min-w-0 space-y-1.5 sm:min-w-56">
              <Label>ກອງສາຂາ</Label>
              <SimpleSelect
                value={reportBranchId}
                onValueChange={setReportBranchId}
                options={branchReportSelectOptions}
                placeholder="ເລືອກສາຂາ"
                className="w-full font-medium"
              />
            </div>
          ) : !hasShopWideBranchAccess && accessibleScopedBranches.length ===
            1 ? (
            <div className="min-w-0 space-y-1.5 sm:min-w-56 sm:self-end">
              <Label>ສາຂາ</Label>
              <p className="rounded-md border border-border bg-muted/30 px-3 py-2 font-medium text-sm">
                {accessibleScopedBranches[0]?.name ?? "—"}
              </p>
            </div>
          ) : !hasShopWideBranchAccess && accessibleScopedBranches.length ===
            0 ? (
            <p className="text-[11px] text-destructive sm:self-end">
              ບໍ່ມີສາຂາໃນສິດ
            </p>
          ) : null}
          <div className="space-y-1.5">
            <Label htmlFor="branch-report-from">ວັນທີເລີ່ມ</Label>
            <Input
              id="branch-report-from"
              type="date"
              value={reportFrom}
              onChange={(e) => setReportFrom(e.target.value)}
              className="w-full font-mono sm:w-44"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="branch-report-to">ວັນທີສິ້ນສຸດ</Label>
            <Input
              id="branch-report-to"
              type="date"
              value={reportTo}
              onChange={(e) => setReportTo(e.target.value)}
              className="w-full font-mono sm:w-44"
            />
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-9"
            onClick={() => {
              setReportFrom("");
              setReportTo("");
              setReportBranchId("__all__");
            }}
          >
            ລ້າງຊ່ວງ
          </Button>
        </div>

        {byBranch.isLoading ? (
          <div className="flex justify-center py-8">
            <Loader />
          </div>
        ) : byBranch.error ? (
          <p className="text-destructive text-sm">
            ໂຫຼດລາຍງານບໍ່ສຳເລັດ — ຕ້ອງເປີດ multi_branch ໃນການຕັ້ງຮ້ານ
          </p>
        ) : byBranch.data && byBranch.data.branches.length > 0 ? (
          <>
            <div className="max-h-96 overflow-auto rounded-md border border-border">
              <table className="w-full min-w-[36rem] text-left text-sm">
                <thead className="sticky top-0 border-border border-b bg-muted/80">
                  <tr>
                    <th className="px-2 py-1.5 font-medium">ສາຂາ</th>
                    {SHOP_CURRENCIES.map((c) => (
                      <th
                        key={c}
                        className="px-2 py-1.5 font-medium tabular-nums"
                      >
                        {c}
                      </th>
                    ))}
                    <th className="px-2 py-1.5 font-medium tabular-nums">ບິນ</th>
                  </tr>
                </thead>
                <tbody>
                  {byBranch.data.branches.map((b) => (
                    <tr
                      key={b.branchId}
                      className="border-border border-b border-dashed"
                    >
                      <td className="px-2 py-1.5 text-xs">
                        {b.branchName?.trim() || b.branchId.slice(0, 8)}
                      </td>
                      {SHOP_CURRENCIES.map((c) => (
                        <td key={c} className="px-2 py-1.5 tabular-nums text-xs">
                          {formatPrice(b.byCurrency[c]?.total ?? 0, c)}
                        </td>
                      ))}
                      <td className="px-2 py-1.5 tabular-nums text-xs text-muted-foreground">
                        {b.includedSaleCount}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              ຂອບເຂດ: <span className="font-medium">{reportBranchScopeLabel}</span>
            </p>
          </>
        ) : (
          <p className="text-muted-foreground text-sm">ບໍ່ມີຂໍ້ມູນໃນຊ່ວງນີ້</p>
        )}
      </CardContent>
    </Card>
  );
}
